import { useState, useEffect, useRef } from 'react';
import { useKleverWallet } from '../hooks/useKleverWallet';
import { usePromoConfig, PromoPlan, PromoToken } from '../context/PromoContext';
import { PaymentToken } from '../context/ConfigContext';

interface PromoPaymentFlowProps {
  plan: PromoPlan;
  token: PromoToken;
  duration: number;
  onSuccess: () => void;
  onCancel: () => void;
}

type PaymentStep = 'select-token' | 'confirm' | 'processing' | 'success' | 'error';

interface PendingPayment {
  memo: string;
  amount: number;
  token: string;
  expiresAt: string;
  receiverWallet: string;
}

export function PromoPaymentFlow({ plan, token, duration, onSuccess, onCancel }: PromoPaymentFlowProps) {
  const { isConnected, address, sendPayment } = useKleverWallet();
  const config = usePromoConfig();
  const [step, setStep] = useState<PaymentStep>('select-token');
  const [selectedPaymentToken, setSelectedPaymentToken] = useState<PaymentToken>(
    config.paymentTokens[0] || { ticker: 'USDC-1LN4', name: 'USDC', precision: 6 }
  );
  const [pendingPayment, setPendingPayment] = useState<PendingPayment | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [txHash, setTxHash] = useState<string | null>(null);

  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8090';
  const discount = config.discounts[duration] || 0;
  const monthlyPrice = plan.priceUsd * (1 - discount);
  const totalPrice = monthlyPrice * duration;

  // Create pending payment on server
  const createPendingPayment = async () => {
    if (!address) return;

    try {
      const response = await fetch(`${apiUrl}/promo/create-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          wallet_address: address,
          ticker: token.ticker,
          plan_slug: plan.slug,
          months: duration,
          payment_token: selectedPaymentToken.ticker,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create payment request');
      }

      const result = await response.json();
      setPendingPayment({
        memo: result.data.payment_memo,
        amount: result.data.amount_token,
        token: result.data.payment_token,
        expiresAt: result.data.expires_at,
        receiverWallet: result.data.receiver_wallet,
      });
      setStep('confirm');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create payment');
      setStep('error');
    }
  };

  // Execute the payment
  const executePayment = async () => {
    if (!pendingPayment || !address) return;

    setStep('processing');
    setError(null);

    try {
      const hash = await sendPayment(
        pendingPayment.receiverWallet,
        pendingPayment.amount,
        pendingPayment.token,
        pendingPayment.memo
      );

      setTxHash(hash);

      // Poll for payment confirmation
      const confirmed = await pollForConfirmation(pendingPayment.memo);

      if (confirmed) {
        setStep('success');
        setTimeout(() => {
          onSuccess();
        }, 2000);
      } else {
        throw new Error('Payment not confirmed within timeout. If you sent the payment, it may still be processing.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Payment failed');
      setStep('error');
    }
  };

  // Poll the server to check if payment was processed
  // Session 1 (2026-05-25, closes PT-P2-10): AbortController + unmount
  // cleanup. Same pattern as PaymentFlow.tsx — see that file for the why.
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  const pollForConfirmation = async (paymentMemo: string): Promise<boolean> => {
    const maxAttempts = 30; // 5 minutes with 10s intervals
    const interval = 10000; // 10 seconds

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    for (let i = 0; i < maxAttempts; i++) {
      if (controller.signal.aborted) {
        return false;
      }
      try {
        const response = await fetch(
          `${apiUrl}/promo/check-payment/${encodeURIComponent(paymentMemo)}`,
          { signal: controller.signal }
        );

        if (response.ok) {
          const result = await response.json();
          if (result.data.status === 'completed') {
            return true;
          }
        }
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') {
          return false;
        }
        // Other errors: continue polling.
      }

      try {
        await new Promise<void>((resolve, reject) => {
          const timer = setTimeout(resolve, interval);
          controller.signal.addEventListener('abort', () => {
            clearTimeout(timer);
            reject(new DOMException('Aborted', 'AbortError'));
          });
        });
      } catch {
        return false;
      }
    }

    return false;
  };

  if (!isConnected) {
    return (
      <div className="payment-flow">
        <div className="error-message">Please connect your wallet first</div>
        <button onClick={onCancel} className="btn btn-outline">
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div className="payment-flow promo-payment">
      {step === 'select-token' && (
        <>
          <h3>Select Payment Token</h3>

          <div className="promo-header">
            <div className="promo-header-info">
              <span className="promo-header-name">{token.name}</span>
              <span className="promo-header-ticker">{token.ticker}</span>
            </div>
            <div className="promo-header-plan">
              {plan.name}
            </div>
          </div>

          <div className="token-options">
            {config.paymentTokens.map(pt => (
              <button
                key={pt.ticker}
                className={`token-option ${selectedPaymentToken.ticker === pt.ticker ? 'selected' : ''}`}
                onClick={() => setSelectedPaymentToken(pt)}
              >
                <span className="token-name">{pt.name}</span>
                <span className="token-ticker">{pt.ticker}</span>
              </button>
            ))}
          </div>

          <div className="payment-summary">
            <div className="summary-row">
              <span>Plan</span>
              <span>{plan.name}</span>
            </div>
            <div className="summary-row">
              <span>Token</span>
              <span>{token.name} ({token.ticker})</span>
            </div>
            <div className="summary-row">
              <span>Duration</span>
              <span>{duration} month{duration > 1 ? 's' : ''}</span>
            </div>
            {discount > 0 && (
              <div className="summary-row discount">
                <span>Discount</span>
                <span>-{(discount * 100).toFixed(0)}%</span>
              </div>
            )}
            <div className="summary-row total">
              <span>Total</span>
              <span>{totalPrice.toFixed(2)} {selectedPaymentToken.name}</span>
            </div>
          </div>

          <div className="button-group">
            <button onClick={onCancel} className="btn btn-outline">
              Cancel
            </button>
            <button onClick={createPendingPayment} className="btn btn-primary">
              Continue
            </button>
          </div>
        </>
      )}

      {step === 'confirm' && pendingPayment && (
        <>
          <h3>Confirm Payment</h3>
          <div className="payment-details">
            <p>You will send:</p>
            <div className="amount-display">
              {pendingPayment.amount.toFixed(2)} {selectedPaymentToken.name}
            </div>
            <p className="promo-confirm-token">
              To promote: <strong>{token.name}</strong>
            </p>
            <p className="memo-note">
              Payment memo: <code>{pendingPayment.memo}</code>
            </p>
            <p className="expiry-note">
              This payment request expires at{' '}
              {new Date(pendingPayment.expiresAt).toLocaleTimeString()}
            </p>
          </div>

          <div className="button-group">
            <button onClick={() => setStep('select-token')} className="btn btn-outline">
              Back
            </button>
            <button onClick={executePayment} className="btn btn-primary">
              Pay with Klever
            </button>
          </div>
        </>
      )}

      {step === 'processing' && (
        <div className="processing-state">
          <div className="spinner"></div>
          <h3>Processing Payment</h3>
          <p>Please confirm the transaction in your Klever wallet...</p>
          {txHash && (
            <p className="tx-hash">
              Transaction: {txHash.slice(0, 12)}...{txHash.slice(-8)}
            </p>
          )}
        </div>
      )}

      {step === 'success' && (
        <div className="success-state">
          <div className="success-icon">✓</div>
          <h3>Promotion Activated!</h3>
          <p>
            <strong>{token.name}</strong> is now being promoted on DexScan.
          </p>
          <p className="success-note">
            Your promotion will be active for {duration} month{duration > 1 ? 's' : ''}.
          </p>
        </div>
      )}

      {step === 'error' && (
        <div className="error-state">
          <div className="error-icon">!</div>
          <h3>Payment Failed</h3>
          <p>{error}</p>
          <div className="button-group">
            <button onClick={onCancel} className="btn btn-outline">
              Cancel
            </button>
            <button onClick={() => setStep('select-token')} className="btn btn-primary">
              Try Again
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
